<template>
  <footer>
    <div class="footer-inner">
      <!-- PC/SP 共通のアコーディオン。カテゴリ見出しはリンクにせずトグルにする（決定#26・#37） -->
      <div class="footer-nav-accordian">
        <div
          v-for="section in accordionSections"
          :key="section.id"
          class="footer-nav-accordian_item"
          :class="{ 'is-open': isOpen(section.id) }"
        >
          <button
            type="button"
            class="footer-nav-accordian_title"
            :aria-expanded="isOpen(section.id)"
            @click="toggleOpen(section.id)"
          >
            {{ section.title }}
            <span class="footer-nav-accordian_icon" aria-hidden="true"></span>
          </button>
          <PartsCollapse :show="isOpen(section.id)">
            <div class="footer-nav-accordian_body">
              <ul class="footer-nav_list">
                <li v-for="link in section.links" :key="link.to + link.label">
                  <PartsChromeLink :to="link.to" :external="link.external">
                    {{ link.label }}
                  </PartsChromeLink>
                </li>
              </ul>
            </div>
          </PartsCollapse>
        </div>
      </div>

      <div class="footer-logo">
        <NuxtLink to="/">
          <img src="/image/common/header/logo.png" alt="オリエンタルバイオ" />
        </NuxtLink>
      </div>

      <div class="footer-bottom">
        <ul class="footer-bottom_links">
          <li v-for="link in FOOTER_BOTTOM_LINKS" :key="link.label">
            <PartsChromeLink :to="link.to" :external="link.external">
              {{ link.label }}
            </PartsChromeLink>
          </li>
        </ul>
        <p class="copyright">© {{ currentYear }} ORIENTALBIO Co.,Ltd.</p>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
/**
 * 支給デザインの `footer`。**PC・SP とも `footer-nav-accordian`（トグル）の1系統**（決定#37）。
 *
 * 支給デザインは PC が `footer-nav`（4カラムのグリッドに全部展開）、SP が `footer-nav-accordian` で、
 * 支給CSSが 768px で出し分けていた。PC もクリックで開く挙動に揃えるため、PC グリッドは丸ごと落とし、
 * アコーディオン1本を両幅で見せる（支給CSSの `display: none` は下の `<style scoped>` で打ち消す）。
 * これで同じリンクがDOMに2組出る状態も解消される。
 *
 * カテゴリ配下の商品名・商品コードは API 由来なのでハードコードしない（決定#5・§3.7）。
 * カテゴリは現行の3分類。4分類化（`personal-care` 改名 ＋ `food` 新設）は別PRで `ProductContentConst` を直すと追随する。
 */
import { useProductContent } from '@/composables/useProductContent';
import { CHROME_LINK_SECTIONS, FOOTER_BOTTOM_LINKS } from '@/consts/ChromeConst';
import { CATEGORY_DISPLAY_ORDER, PRODUCT_CONTENT_CONSTANTS } from '@/consts/ProductContentConst';
import type { ChromeLink, ChromeLinkSection } from '@/types/domains/ChromeType';
import type { ProductCategoryType } from '@/types/domains/index';

const { getProductContentsByCategory } = useProductContent();

const currentYear: number = new Date().getFullYear();

/** カテゴリごとの商品リンク。カテゴリIDで引ける形にする。表示名・商品コードは API の値をそのまま使う */
const categorySectionById = computed<Record<number, ChromeLinkSection>>(() =>
  Object.fromEntries(
    Object.entries(PRODUCT_CONTENT_CONSTANTS.CATEGORY_ENGLISH_NAME).map(([id, englishName]) => [
      Number(id),
      {
        id: englishName,
        title: PRODUCT_CONTENT_CONSTANTS.CATEGORY_JAPANESE_NAME[id],
        links: getProductContentsByCategory(
          englishName as ProductCategoryType.CategoryEnglishName
        ).map<ChromeLink>(({ webTitle, productCode }) => ({
          label: webTitle,
          to: `/products/${productCode}`,
        })),
      },
    ])
  )
);

/** 支給デザインの表示順（サプリメント → 食品 → スキンケア → パーソナルケア）。ID の昇順ではない */
const categorySections = computed<ChromeLinkSection[]>(() =>
  CATEGORY_DISPLAY_ORDER.map((id) => categorySectionById.value[id]).filter(Boolean)
);

const accordionSections = computed<ChromeLinkSection[]>(() => [
  ...categorySections.value,
  ...CHROME_LINK_SECTIONS,
]);

/** アコーディオンの開閉。PC・SP で同じ状態を使うので、幅が変わっても開いたままにする */
const openSections = ref<Set<string>>(new Set());
const isOpen = (id: string): boolean => openSections.value.has(id);
const toggleOpen = (id: string): void => {
  const next = new Set(openSections.value);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  openSections.value = next;
};
</script>

<style scoped>
/* 支給CSSは `.footer-nav-accordian` を 768px 以下でしか出さない
   （`renewal/style.css:2409-2414` で `display: none`、`:2476-2478` で SP のみ `flex`）。
   PC でも同じアコーディオンにするため常時表示に戻す（決定#37）。
   グローバルな `vue-adjust.css` ではなくここに置くのは、同じクラス名をハンバーガーメニュー
   （`organisms/layouts/header/index.vue`）も使っており、そちらへ波及させないため。 */
.footer-nav-accordian {
  display: flex;
}

/* 支給の `margin-bottom: 12.1vw` / `gap: 5.2vw` は SP 前提の値が base 側に書かれており、
   PC 幅では 200px 超になる。SP での実寸（375px 幅で約20px / 約45px）と、
   捨てた PC グリッド `.footer-nav { margin-bottom: 80px }` の余白に合わせて PC だけ上書きする */
@media (min-width: 769px) {
  .footer-nav-accordian {
    gap: 20px;
    margin-bottom: 80px;
  }

  /* PC は開いた中身を多カラムで横に流す（決定#41・Figma 7/17【提案①】の「ぜんぶ開いた時」）。
     SP は支給どおり1リンク1行なので、この規則は 769px 以上にだけ掛ける。

     grid ではなく multi-column を使うのは、Figma の並びが列ごとに上から下へ読む順（列優先）で、
     grid の既定（行優先）とは流れが逆になるため。`columns` は各列を上から埋めて折り返す。

     列数はカテゴリごとに決め打ちしない。Figma の列構成は 2〜5列でピッチも不統一（食品 約355px /
     お買い物サポート 約202px）、同じカテゴリでも「デフォルト表示」と「ぜんぶ開いた時」で列幅が違う。
     手置きの値であり、カテゴリ配下の商品リンクは API 由来で件数が変動する（決定#5）ため再現しない。

     `200px` は Figma で最も密な お買い物サポート／コンテンツ の列ピッチ（約202px）に合わせた値。
     本文幅 1000px でおよそ4列になる。最長ラベル（`KENCOSフレーバーカートリッジ　タバコフレーバー
     （1本入り）` 380px）を下限にすると2列までしか入らず Figma の見た目から離れるので採らない。
     長いラベルは列内で折り返す。実寸の確認は人間レビューで行う（決定#41）。 */
  .footer-nav-accordian_body .footer-nav_list {
    columns: 200px auto;
    column-gap: 30px;
  }

  /* 1リンクが列をまたいで割れないようにする */
  .footer-nav-accordian_body .footer-nav_list li {
    break-inside: avoid;
  }
}
/* フッター最下段。支給CSS は PC で `.footer-bottom_links` を折り返さず（`renewal/style.css:741`）、
   `.copyright` を `position: absolute; right: 0` で右端に置く（`:752-756`）。リンクが6本になり、
   1200px 以下ではリンク列がコピーライトと重なる（769〜約900px は支給CSS の時点から重なっていた）。
   769〜1200px は支給の SP（`:773-785`）と同じ縦組みにして、コピーライトをリンク列の下に置く。
   境目の 1200px は、支給CSS がフッターの余白を切り替える幅（`:758`）に揃えた。
   1201px 以上は支給どおりで、リンク列とコピーライトの間は約10px しかないため、リンクを増やすときは境目を見直す。
   `.copyright` はハンバーガー（`header/index.vue`）も使うので、グローバル調整層ではなくここに置く。 */
@media (min-width: 769px) and (max-width: 1200px) {
  .footer-bottom {
    flex-direction: column;
    gap: 12px;
  }

  .footer-bottom_links {
    flex-wrap: wrap;
    justify-content: center;
    row-gap: 8px;
  }

  .copyright {
    position: static;
  }
}
</style>
